import { Injectable } from '@angular/core';
import { ReleaseEnvironment } from '../models/release-environment';
import { DeployPhase } from '../models/deploy-phase';
import { DeployPhaseListComparison } from '../models/deploy-phase-list-comparison';
import { EnvironmentListComparison } from '../models/environment-list-comparison';
import { WorkflowTask } from '../models/workflow-task';
import { WorkflowTaskListComparison } from '../models/workflow-task-list-comparison';
import { KeyValue } from '../models/key-value';

@Injectable({
  providedIn: 'root'
})
export class TaskComparerService {
  private readonly added: string = 'added';
  private readonly deleted: string = 'deleted';
  private readonly edited: string = 'edited';
  private readonly unchanged: string = 'unchanged';
  private readonly modified: string = 'modified';

  constructor() { }

  compare(before: ReleaseEnvironment[], after: ReleaseEnvironment[]): EnvironmentListComparison {

    console.log('Comparing environments...');

    before.forEach(b => { b.status = this.unchanged; b.childStatus = this.unchanged });
    after.forEach(a => { a.status = this.unchanged; a.childStatus = this.unchanged });

    const deletedEnvironments: ReleaseEnvironment[] = this.difference(before, after, (b, a) => b.id === a.id).map(d => { d.status = this.deleted; return d; });
    const addedEnvironments: ReleaseEnvironment[] = this.difference(after, before, (a, b) => a.id === b.id).map(a => { a.status = this.added; return a; });
    const editedBeforeEnvironments: ReleaseEnvironment[] = this.intersect(before, after, (b, a) => b.id === a.id && (b.name !== a.name)).map(e => { e.status = this.edited; return e; });
    const editedAfterEnvironments: ReleaseEnvironment[] = this.intersect(after, before, (a, b) => a.id === b.id && (a.name !== b.name)).map(e => { e.status = this.edited; return e; });
    const unchangedEnvironments: ReleaseEnvironment[] = this.intersect(before, after, (b, a) => b.id === a.id && b.name === a.name);

    const coexistingEnvironmentIds = [...editedBeforeEnvironments, ...unchangedEnvironments].map(e => e.id);
    const beforeUnchangedEnvironments = before.filter(b => coexistingEnvironmentIds.includes(b.id));
    const afterUnchangedEnvironments = after.filter(a => coexistingEnvironmentIds.includes(a.id));

    const unchangedEnvironmentsWithDeployPhaseChanges = this.processDeployPhases(beforeUnchangedEnvironments, afterUnchangedEnvironments);

    const result = new EnvironmentListComparison(
      [...deletedEnvironments, ...editedBeforeEnvironments, ...unchangedEnvironmentsWithDeployPhaseChanges.before],
      [...addedEnvironments, ...editedAfterEnvironments, ...unchangedEnvironmentsWithDeployPhaseChanges.after]
    );

    result.before = [...new Set(result.before)].sort((a, b) => a.rank - b.rank);
    result.after = [...new Set(result.after)].sort((a, b) => a.rank - b.rank);;

    return result;
  }

  processDeployPhases(beforeEnvironments: ReleaseEnvironment[], afterEnvironments: ReleaseEnvironment[]): EnvironmentListComparison {
    console.log('Comparing deploy phases...');

    beforeEnvironments.forEach(beforeEnvironment => {
      const afterEnvironment = afterEnvironments.find(a => a.name === beforeEnvironment.name);

      if (afterEnvironment) {
        beforeEnvironment.deployPhases.forEach(dp => { dp.status = this.unchanged; dp.childStatus = this.unchanged });
        afterEnvironment.deployPhases.forEach(dp => { dp.status = this.unchanged; dp.childStatus = this.unchanged });

        let deletedPhases: DeployPhase[] = this.difference(beforeEnvironment.deployPhases, afterEnvironment.deployPhases, (b, a) => b.name === a.name)
          .map(d => { d.status = this.deleted; return d; });

        let addedPhases: DeployPhase[] = this.difference(afterEnvironment.deployPhases, beforeEnvironment.deployPhases, (a, b) => a.name === b.name)
          .map(a => { a.status = this.added; return a; });

        const unchangedPhases: DeployPhase[] = this.intersect(beforeEnvironment.deployPhases, afterEnvironment.deployPhases, (b, a) => b.name === a.name);
        const coexistingPhaseNames = [...unchangedPhases].map(e => e.name);
        const beforeUnchangedPhases = beforeEnvironment.deployPhases.filter(dp => coexistingPhaseNames.includes(dp.name));
        const afterUnchangedPhases = afterEnvironment.deployPhases.filter(dp => coexistingPhaseNames.includes(dp.name));

        const unchangedPhasesWithTaskChanges = this.processWorkflowTasks(beforeUnchangedPhases, afterUnchangedPhases);

        beforeEnvironment.childStatus = this.getEnvironmentChildStatus(deletedPhases, unchangedPhasesWithTaskChanges.before);
        afterEnvironment.childStatus = this.getEnvironmentChildStatus(addedPhases, unchangedPhasesWithTaskChanges.after);

        beforeEnvironment.deployPhases = [...deletedPhases, ...unchangedPhasesWithTaskChanges.before];
        afterEnvironment.deployPhases = [...addedPhases, ...unchangedPhasesWithTaskChanges.after];
      }
    });

    return new EnvironmentListComparison(beforeEnvironments, afterEnvironments);
  }

  processWorkflowTasks(before: DeployPhase[], after: DeployPhase[]): DeployPhaseListComparison {
    console.log('Comparing tasks...');

    before.forEach(beforePhase => {
      const afterPhase = after.filter(d => d.name === beforePhase.name && d.rank === beforePhase.rank)[0];

      if (afterPhase) {
        beforePhase.workflowTasks.forEach(wft => { wft.status = this.unchanged; wft.childStatus = this.unchanged });
        afterPhase.workflowTasks.forEach(wft => { wft.status = this.unchanged; wft.childStatus = this.unchanged });

        let deletedTasks: WorkflowTask[] = this.difference(beforePhase.workflowTasks, afterPhase.workflowTasks, (a, b) => a.name === b.name)
          .map(d => { d.status = this.deleted; return d; });

        let addedTasks: WorkflowTask[] = this.difference(afterPhase.workflowTasks, beforePhase.workflowTasks, (a, b) => a.name === b.name)
          .map(a => { a.status = this.added; return a; });

        const unchangedTasks: WorkflowTask[] = this.intersect(beforePhase.workflowTasks, afterPhase.workflowTasks, (a, b) => a.name === b.name);
        const coexistingTasks = [...unchangedTasks].map(e => e.name);
        const beforeUnchangedTasks = beforePhase.workflowTasks.filter(wft => coexistingTasks.includes(wft.name));
        const afterUnchangedTasks = afterPhase.workflowTasks.filter(wft => coexistingTasks.includes(wft.name));

        const unchangedTasksWithInputChanges = this.processInputs(beforeUnchangedTasks, afterUnchangedTasks);

        beforePhase.childStatus = this.getDeployPhaseChildStatus(deletedTasks, unchangedTasksWithInputChanges.before);
        afterPhase.childStatus = this.getDeployPhaseChildStatus(addedTasks, unchangedTasksWithInputChanges.after);

        beforePhase.workflowTasks = [...deletedTasks, ...unchangedTasksWithInputChanges.before];
        afterPhase.workflowTasks = [...addedTasks, ...unchangedTasksWithInputChanges.after];
      }
    });

    return new DeployPhaseListComparison(before, after);
  }

  processInputs(before: WorkflowTask[], after: WorkflowTask[]): WorkflowTaskListComparison {
    console.log('Comparing inputs...');

    before.forEach(beforeTask => {
      const afterTasks = after.filter(a => a.name === beforeTask.name);
      let afterTask: WorkflowTask | undefined;

      if (afterTasks.length > 1) {
        afterTask = this.getTaskByIndex(beforeTask, before, after);
      } else {
        afterTask = afterTasks[0];
      }

      if (afterTask) {
        beforeTask.inputs.forEach(i => i.status = this.unchanged);
        afterTask.inputs.forEach(i => i.status = this.unchanged);
        const ignoredKeys = ["ConnectedServiceName","DnsArmSpn",
          "awsCredentials", "azureSubscription", "azureResourceManagerConnection",
          "connectedServiceNameARM", "SPN", "ConnectedService"]
          .map(k => k.toLowerCase());
        let deletedInputs: KeyValue[] = this.difference(beforeTask.inputs, afterTask.inputs, (a, b) => a.key === b.key).map(d => { d.status = this.deleted; return d; });
        let addedInputs: KeyValue[] = this.difference(afterTask.inputs, beforeTask.inputs, (a, b) => a.key === b.key).map(a => { a.status = this.added; return a; });
        let editedBeforeInputs: KeyValue[] = this.intersect(beforeTask.inputs, afterTask.inputs, (a, b) => a.key === b.key && (a.value !== b.value) && !ignoredKeys.includes(a.key.toLowerCase()))
          .map(e => { e.status = this.edited; return e; });
        let editedAfterInputs: KeyValue[] = this.intersect(afterTask.inputs, beforeTask.inputs, (a, b) => a.key === b.key && (a.value !== b.value) && !ignoredKeys.includes(a.key.toLowerCase()))
          .map(e => { e.status = this.edited; return e; });

        const unchangedInputs: KeyValue[] = this.intersect(beforeTask.inputs, afterTask.inputs, (a, b) => (a.key === b.key && a.value === b.value) || ignoredKeys.includes(a.key.toLowerCase()));

        beforeTask.childStatus = this.getTaskChildStatus(deletedInputs, editedBeforeInputs);
        afterTask.childStatus = this.getTaskChildStatus(addedInputs, editedAfterInputs);

        beforeTask.inputs = [...deletedInputs, ...editedBeforeInputs, ...unchangedInputs];
        afterTask.inputs = [...addedInputs, ...editedAfterInputs, ...unchangedInputs];
      }
    });

    return new WorkflowTaskListComparison(before, after);
  }

  getEnvironmentChildStatus(phases: DeployPhase[], phaseListComparison: DeployPhase[]): string {
    return phases.length > 0 || phaseListComparison.some(a => a.childStatus === this.modified) ? this.modified : this.unchanged;
  };

  getDeployPhaseChildStatus(tasks: WorkflowTask[], taskListComparison: WorkflowTask[]): string {
    return tasks.length > 0 || taskListComparison.some(a => a.childStatus === this.modified) ? this.modified : this.unchanged;
  }

  getTaskChildStatus(inputs: KeyValue[], inputListComparison: KeyValue[]): string {
    return inputs.length > 0 || inputListComparison.length > 0 ? this.modified : this.unchanged;
  }

  getTaskByIndex(beforeTask: WorkflowTask, beforeTasks: WorkflowTask[], afterTasks: WorkflowTask[]): WorkflowTask {
    let index = beforeTasks
      .filter(b => b.name === beforeTask.name)
      .indexOf(beforeTask);
    return afterTasks[index];
  }

  difference(listA: any[], listB: any[], predicate: (x: any, y: any) => boolean): any[] {
    return listA.filter(a => listB.filter(b => {
      return predicate(a, b);
    }).length === 0);
  }

  intersect(listA: any[], listB: any[], predicate: (x: any, y: any) => boolean): any[] {
    return listA.filter(a => listB.filter(b => {
      return predicate(a, b);
    }).length > 0);
  }
}
